import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import { useTranslation } from 'react-i18next'
import { useAuth } from './account/AuthContext'
import { requestOtp } from '../shared/api/account'
import { setStaffSession } from '../shared/api/ownerClient'
import { errorMessage } from '../shared/api/errorMessage'
import { isPhone, isPersonName } from '../shared/validation'
import { unlockAudio } from '../owner/sound'

/**
 * The shop's single front door (D-066/D-084): one phone-number flow for the owner, staff, OR a
 * customer. Everyone signs in with an SMS code — send a code, then verify it. The realm is resolved
 * server-side by the phone (staff precedence, D-084), never a client-side hint: `verify` returns a
 * `realm`, and STAFF is seeded into the separate owner session store here (the only cross-realm
 * step, kept at this one page). No password, no separate register or owner-login page.
 *
 * A brand-new phone becomes a customer account on first verify, but the name is compulsory: verify
 * returns `needsName` (the code stays live), we reveal a required name field, and the same code is
 * resubmitted with it. Returning customers and staff never see that field.
 */
export default function LoginPage() {
  const { t } = useTranslation()
  const { verifyOtp } = useAuth()
  const navigate = useNavigate()
  // Set by a page that sent the customer here (checkout): go back there after login.
  const from = (useLocation().state as { from?: string } | null)?.from
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [needsName, setNeedsName] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Android Chrome (WebOTP): once a code is sent, listen for the SMS and fill the field. It only
  // fires when the SMS's last line is "@<this host> #<code>", which needs a Fast2SMS template with
  // that line (see FAST2SMS_OTP_VARIABLES); until then it silently never resolves. iOS uses the
  // autoComplete="one-time-code" hint on the field instead.
  useEffect(() => {
    if (!otpSent || !('OTPCredential' in window)) return
    const ac = new AbortController()
    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal } as CredentialRequestOptions)
      .then((c) => {
        const otp = (c as unknown as { code?: string } | null)?.code
        if (otp) setCode(otp)
      })
      .catch(() => {}) // aborted, denied or unsupported: the user types the code
    return () => ac.abort()
  }, [otpSent])

  const onPhoneChange = (value: string) => {
    setPhone(value)
    // Target changed — any code we sent is for the old number; drop the whole verify cycle.
    setOtpSent(false)
    setNeedsName(false)
    setCode('')
    setName('')
    setError(null)
  }

  const sendCode = async () => {
    if (!isPhone(phone)) {
      setError(t('validation.phone'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await requestOtp(phone.trim())
      setOtpSent(true)
      setNeedsName(false) // fresh code → fresh verify cycle
    } catch (err) {
      // The server's throttle reason ("please wait…") is safe to show and useful (D-062).
      setError(errorMessage(err, t('auth.login.sendCodeError')))
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    if (!phone.trim()) {
      setError(t('auth.login.enterCreds'))
      return
    }
    // Step 1: send the code.
    if (!otpSent) {
      await sendCode()
      return
    }
    // Step 2: verify it.
    if (!code.trim()) {
      setError(t('auth.login.enterCode'))
      return
    }
    // Step 3 (new customers only): the name field has appeared and is required.
    if (needsName && !name.trim()) {
      setError(t('auth.login.enterName'))
      return
    }
    if (needsName && !isPersonName(name)) {
      setError(t('validation.name'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      // Unlock audio on this user gesture so a staff board's new-order chime is allowed later —
      // done before the realm is known (harmless for a customer), since the gesture ends at the await.
      unlockAudio()
      const res = await verifyOtp(phone.trim(), code.trim(), name.trim() || undefined)
      if (res.needsName) {
        // Brand-new customer: the code is valid and still live, but a name is required. Reveal the
        // field and let them resubmit the SAME code — no new SMS, nothing created until they do.
        setNeedsName(true)
        setSubmitting(false)
        return
      }
      if (res.realm === 'STAFF' && res.staff) {
        // Seed the in-memory staff store — OwnerShell reads it synchronously on mount.
        setStaffSession(res.staff)
        navigate('/manage', { replace: true })
      } else {
        navigate(from ?? '/account', { replace: true })
      }
    } catch {
      // A wrong/expired code reveals nothing about account existence (verify find-or-creates or
      // resolves staff), so a specific message is fine here — just not the raw server text.
      setError(t('auth.login.invalidCode'))
      setSubmitting(false)
    }
  }

  const submitLabel = submitting
    ? otpSent
      ? t('auth.login.verifying')
      : t('auth.login.sending')
    : otpSent
      ? t('auth.login.verify')
      : t('auth.login.sendCode')

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        {t('auth.login.title')}
      </Typography>
      <Stack spacing={2}>
        {from === '/checkout' && <Alert severity="info">{t('auth.login.toCheckout')}</Alert>}
        <TextField
          label={t('auth.login.identifier')}
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          fullWidth
          autoComplete="tel"
          inputProps={{ inputMode: 'tel' }}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />

        {otpSent && (
          <>
            <Alert severity="success">{t('auth.login.codeSent', { phone: phone.trim() })}</Alert>
            <TextField
              label={t('auth.login.codeLabel')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              autoFocus
              autoComplete="one-time-code"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
            {needsName && (
              <>
                <Alert severity="info">{t('auth.login.nameNewHint')}</Alert>
                <TextField
                  label={t('common.yourName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                  autoFocus
                  autoComplete="name"
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                />
              </>
            )}
            <Button variant="text" size="small" onClick={sendCode} disabled={submitting} sx={{ alignSelf: 'flex-start' }}>
              {t('auth.login.resend')}
            </Button>
          </>
        )}

        {error && <Alert severity="error">{error}</Alert>}
        <Button variant="contained" size="large" fullWidth disabled={submitting} onClick={handleSubmit}>
          {submitLabel}
        </Button>

        {!otpSent && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('auth.login.otpHint')}
          </Typography>
        )}
      </Stack>
    </Container>
  )
}
